import { useAvvenimentoCompetitors, useIsLivePage } from 'features/sport/hooks';

import { CompetitorIcon } from 'components/scommesseTable/scommesseTableCompetitors';
import React from 'react';
import { SkeletonArea } from 'components/skeletonArea';
import { SportsCompetitor } from 'types/swagger';
import { breakpoint } from 'utility/constant';
import classNames from 'classnames';
import styles from './SportAvvenimentoHeaderTitle.module.scss';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

export interface SportAvvenimentoHeaderTitleProps {
  className?: string;
  avvenimentoKey?: string;
}

const CompetitorComponent = ({
  iconSrc,
  isFirst,
  descrizione,
  descrizioneTrKey,
}: SportsCompetitor & { isFirst?: boolean }) => {
  const { t } = useTranslation();
  const descrizioneTxt = t(descrizioneTrKey, descrizione);
  return (
    <div className={classNames(styles.competitor, { [styles.first]: isFirst })} suppressHydrationWarning>
      {iconSrc && (
        <div className={styles.imageContainer} title={descrizioneTxt} suppressHydrationWarning>
          <CompetitorIcon className={styles.image} iconSrc={iconSrc} />
        </div>
      )}
      <span className={styles.name} suppressHydrationWarning>
        {descrizioneTxt}
      </span>
    </div>
  );
};

export const SportAvvenimentoHeaderTitle = ({ className }: SportAvvenimentoHeaderTitleProps) => {
  const isLive = useIsLivePage();
  const { t } = useTranslation();

  const { doCheck } = useWindowSize();
  const [isMobile, isMD] = doCheck([
    { comparer: enComparer.less, size: breakpoint.sm },
    { comparer: enComparer.greater | enComparer.equal, size: breakpoint.md },
  ]);

  const {
    ora,
    data,
    isLoading,
    competitors,
    descrizione,
    descrizioneTrKey,
    descrizioneManifestazione,
    descrizioneManifestazioneTrKey,
  } = useAvvenimentoCompetitors();

  if (isLoading) {
    return (
      <div className={classNames(styles.containerSkeleton, className)}>
        <SkeletonArea height={isMobile ? '0' : '1.6'} className={styles.labelSkeleton} delay={2} />
        <SkeletonArea height={isMobile ? '2.8' : '3.2'} className={styles.competitorSkeleton} delay={2} />
        <SkeletonArea height={isMobile ? '2.8' : '3.2'} className={styles.listSkeleton} delay={2} />
        <SkeletonArea height={isMobile ? '2.8' : '3.2'} className={styles.competitorSkeleton} delay={2} />
      </div>
    );
  }

  return (
    <div
      className={classNames(styles.container, className ?? '', {
        [styles.small]: isMobile,
        [styles.onlyTitle]: !competitors?.length && descrizione,
      })}
    >
      <span className={classNames(styles.label, { [styles.hidden]: !isMD })} suppressHydrationWarning={true}>
        {t(descrizioneManifestazioneTrKey, descrizioneManifestazione)}
      </span>
      {!!competitors?.length ? (
        <React.Fragment>
          {competitors?.length > 0 && <CompetitorComponent {...competitors[0]} isFirst />}
          <ul
            className={classNames(styles.list, isLive ? styles.horizontal : styles.vertical)}
            suppressHydrationWarning
          >
            {isLive ? (
              <li className={styles.highlighted} suppressHydrationWarning>
                -
              </li>
            ) : (
              <React.Fragment>
                <li className={styles.item} suppressHydrationWarning>
                  {data}
                </li>
                <li className={styles.item} suppressHydrationWarning>
                  {ora}
                </li>
              </React.Fragment>
            )}
          </ul>
          {competitors?.length > 1 && <CompetitorComponent {...competitors[1]} />}
        </React.Fragment>
      ) : (
        descrizione && <h2 className={styles.descrizione}>{t(descrizioneTrKey, descrizione)}</h2>
      )}
    </div>
  );
};
